/* eslint-disable no-throw-literal */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { usePermission } from "../../../shared";
import { AppContext } from "../../../store/AppScopeProvider";
import { errorMessage, successMessage } from "../../../utils";
import {
  approveClientAccountRequestApi,
  changeClientAccountsStatusApi,
  createClientAccount,
  getClientAccountRequestDetailsApi,
  getClientAccountsDetailsByID,
  updateClientAccount,
} from "../api";
import { AuthContext } from "../../../store";

export const useClientAccountDetails = ({ id }) => {
  const navigate = useNavigate();
  const { requestId } = useParams();

  const { pathname } = useLocation();
  const { appState } = React.useContext(AppContext);
  const { auth } = React.useContext(AuthContext);
  const { globalData } = appState;

  const [state, setState] = useImmer({
    isLoading: true,
    details: {},
    isSaveButtonBusy: false,
    isRejectDialogOpen: false,
    updateData: {
      account_name: "",
      abn_number: "",
      store_id: "",
      status: 1,
      addresses: [
        {
          name: "",
          address: "",
          email: "",
          phone: "",
          country_code: 61,
        },
      ],
      statement_emails: [],
      invoice_emails: [],
      credit_limit: null,
      restrict_usage: 1,
      bill_duration: null,
      bill_period: null,
      bill_day: null,
      bill_date: null,
      bill_date2: null,
      carry_forward: 0,
      due_days: 7,
    },
  });
  const [value, setValue] = React.useState(2);

  // Permission checking
  const isEditable = usePermission("client-account-modify");

  const isStoreAdmin = pathname.includes("store-admin");
  const isRequestDetailsPage = requestId && pathname.includes("/request");
  const isEditPage = id && !isRequestDetailsPage;
  const isCreatePage = !id && !isRequestDetailsPage;

  // ======================================================== API SECTION =================================================
  const getClientAccountRequestDetails = async (requestId) => {
    triggerFormLoading(true);
    try {
      const response = await getClientAccountRequestDetailsApi(requestId);
      const { success, message, data } = response;
      if (success) {
        console.log(data);
        setState((draft) => {
          draft.updateData = { ...draft.updateData, ...data };
        });
      } else {
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to fetch the client account request";

      errorMessage(message);
    } finally {
      triggerFormLoading(false);
    }
  };

  const handleClientAccountDetails = async (id) => {
    try {
      const res = await getClientAccountsDetailsByID(id);
      if (res.success) {
        setState((draft) => {
          draft.details = res.data;
          draft.isLoading = false;
          draft.updateData = {
            ...res.data,
            bill_date: res.data.bill_day ? null : res.data.bill_date,
            bill_date2: res.data.bill_day ? res.data.bill_date : null,
          }
        });
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const onCreateClientAccount = async () => {
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });

      let data = {
        ...state.updateData,
        store_id: isStoreAdmin
          ? auth.data.selectedStoreId
          : state.updateData.store_id,
        status: state.updateData.status,
        restrict_usage: state.updateData.restrict_usage,
        due_amount: state.updateData.due_amount,
        credit_limit: state.updateData.credit_limit,
        carry_forward: state.updateData.carry_forward,
        bill_duration: state.updateData.bill_duration,
        bill_period: state.updateData.bill_period,
      };

      if (state.updateData.bill_period === 1) {
        data = {
          ...data,
          bill_date: null,
          bill_day: state.updateData.bill_day,
        };
      } else if (state.updateData.bill_period === 3) {
        data = {
          ...data,
          bill_period: state.updateData.bill_period,
          bill_duration: state.updateData.bill_duration,
          bill_date: null,
          bill_day: null,
        };
      } else if (state.updateData.bill_period === 2) {
        if (value === 1) {
          data = {
            ...data,
            bill_date: state.updateData.bill_date,
            bill_date2: null,
            bill_day: null,
          };
        } else {
          data = {
            ...data,
            bill_date: state.updateData.bill_date2,
            bill_date2: null,
            bill_day: state.updateData.bill_day,
          };
        }
      }
      const res = await createClientAccount(data);
      if (res.success) {
        successMessage(res.message);
        navigate(
          isStoreAdmin ? `/store-admin/client-account-v2` : `/client-account-v2`
        );
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  const onUpdateClientAccount = async () => {
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });

      let data = {
        ...state.updateData,
        store_id: isStoreAdmin
          ? auth.data.selectedStoreId
          : state.updateData.store_id,
        status: state.updateData.status,
        restrict_usage: state.updateData.restrict_usage,
        due_amount: state.updateData.due_amount,
        credit_limit: state.updateData.credit_limit,
        carry_forward: state.updateData.carry_forward,
        bill_duration: state.updateData.bill_duration,
        bill_period: state.updateData.bill_period,
      };

      if (state.updateData.bill_period === 1) {
        data = {
          ...data,
          bill_date: null,
          bill_day: state.updateData.bill_day,
        };
      } else if (state.updateData.bill_period === 3) {
        data = {
          ...data,
          bill_period: state.updateData.bill_period,
          bill_duration: state.updateData.bill_duration,
          bill_date: null,
          bill_day: null,
        };
      } else if (state.updateData.bill_period === 2) {
        if (value === 1) {
          data = {
            ...data,
            bill_date: state.updateData.bill_date,
            bill_date2: null,
            bill_day: null,
          };
        } else {
          data = {
            ...data,
            bill_date: state.updateData.bill_date2,
            bill_date2: null,
            bill_day: state.updateData.bill_day,
          };
        }
      }
      const res = await updateClientAccount(id, data);
      if (res.success) {
        successMessage(res.message);
        navigate(
          isStoreAdmin
            ? `/store-admin/client-account-v2/details/${id}`
            : `/client-account-v2/details/${id}`
        );
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  useEffect(() => {
    if (!state.isLoading) {
      if (value === 1) {
        setState((draft) => {
          draft.updateData.bill_date2 = "";
          draft.updateData.bill_day = "";
        });
      }
      if (value === 2) {
        setState((draft) => {
          draft.updateData.bill_date = "";
        });
      }
    }
  }, [value]);

  const approveClientAccountRequest = async ({ requestId, payload }) => {
    triggerSaveButtonLoading(true);
    try {
      const response = await approveClientAccountRequestApi({
        requestId,
        payload,
      });
      const { success, message } = response;
      if (success) {
        successMessage(message);
        navigate(
          isStoreAdmin
            ? "/store-admin/client-account-v2"
            : "/client-account-v2",
          { replace: true }
        );
      } else {
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to approve the client account request";

      errorMessage(message);
    } finally {
      triggerSaveButtonLoading(false);
    }
  };

  // ====================================================== UTIL FUNCTION SECTION =========================================
  const triggerSaveButtonLoading = (loading) => {
    setState((draft) => {
      draft.isSaveButtonBusy = loading;
    });
  };

  const triggerFormLoading = (loading) => {
    setState((draft) => {
      draft.isLoading = loading;
    });
  };

  const triggerRejectDialog = (open) => {
    setState((draft) => {
      draft.isRejectDialogOpen = open;
    });
  };

  const handleChange = (event, index) => {
    const { name, value } = event.target;
    if (["name", "address", "email", "phone", "country_code"].includes(name)) {
      setState((draft) => {
        draft.updateData = {
          ...draft.updateData,
          addresses: draft.updateData.addresses.map((v, i) =>
            i === index ? { ...v, [name]: value } : v
          ),
        };
      });
    }
    if (name === "status" || name === "restrict_usage") {
      setState((draft) => {
        draft.updateData = {
          ...draft.updateData,
          [name]: event.target.checked ? 1 : 0,
        };
      });
    } else if (["statement_emails", "invoice_emails"].includes(name)) {
      setState((draft) => {
        const currentArray = new Set(draft.updateData[name] || []);
        currentArray.add(value);
        draft.updateData = {
          ...draft.updateData,
          [name]: [...currentArray],
        };
      });
    } else {
      setState((draft) => {
        draft.updateData = {
          ...draft.updateData,
          [name]: value,
        };
      });
    }
  };

  const onDeleteEmails = (e) => {
    const { name, value } = e;

    setState((draft) => {
      const currentArray = new Set(draft.updateData[name] || []);
      currentArray.delete(value);
      draft.updateData = {
        ...draft.updateData,
        [name]: [...currentArray],
      };
    });
  };

  const onDayPicker = (value) => {
    if (state.updateData.bill_day === value) {
      setState((draft) => {
        draft.updateData.bill_day = null;
      });
    } else {
      setState((draft) => {
        draft.updateData.bill_day = value;
      });
    }
  };

  const handleAddressSection = (type, index) => {
    setState((draft) => {
      if (type === "add") {
        draft.updateData.addresses.push({
          name: "",
          address: "",
          email: "",
          phone: "",
          country_code: 61,
        });
      } else if (type === "remove" && draft.updateData.addresses.length > 1) {
        draft.updateData.addresses.splice(index, 1);
      }
    });
  };

  const handleApproveRequest = async () => {
    let data = {
      ...state.updateData,
      store_id: isStoreAdmin
        ? auth.data.selectedStoreId
        : state.updateData.store_id,
      status: state.updateData.status,
      restrict_usage: state.updateData.restrict_usage,
      due_amount: state.updateData.due_amount,
      credit_limit: state.updateData.credit_limit,
      carry_forward: state.updateData.carry_forward,
      bill_duration: state.updateData.bill_duration,
      bill_period: state.updateData.bill_period,
    };

    if (state.updateData.bill_period === 1) {
      data = {
        ...data,
        bill_date: null,
        bill_day: state.updateData.bill_day,
      };
    } else if (state.updateData.bill_period === 3) {
      data = {
        ...data,
        bill_period: state.updateData.bill_period,
        bill_duration: state.updateData.bill_duration,
        bill_date: null,
        bill_day: null,
      };
    } else if (state.updateData.bill_period === 2) {
      if (value === 1) {
        data = {
          ...data,
          bill_date: state.updateData.bill_date,
          bill_date2: null,
          bill_day: null,
        };
      } else {
        data = {
          ...data,
          bill_date: state.updateData.bill_date2,
          bill_date2: null,
          bill_day: state.updateData.bill_day,
        };
      }
    }

    await approveClientAccountRequest({ requestId, payload: data });
  };

  // ============================================================== USE EFFECT SECTION =======================================
  React.useEffect(() => {
    if (isEditPage) {
      handleClientAccountDetails(id);
    }
    if (isRequestDetailsPage) {
      getClientAccountRequestDetails(requestId);
    }
  }, []);

  const HandleClientAccountStatus = async (id) => {
    try {
      const res = await changeClientAccountsStatusApi(id);
      if (res.success) {
        setState((draft) => {
          draft.details.status =
            state?.details?.status === 1 ? 0 : 1;
        });
        successMessage(res.message);
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  return {
    state,
    isEditable,
    isStoreAdmin,
    globalData,
    value,
    setValue,
    handleChange,
    onDayPicker,
    onCreateClientAccount,
    handleAddressSection,
    onDeleteEmails,
    onUpdateClientAccount,
    HandleClientAccountStatus,

    // Manage requests
    isRequestDetailsPage,
    isEditPage,
    isCreatePage,
    triggerRejectDialog,
    handleApproveRequest,
  };
};
